import { appNow } from "./appClock";
import { composeStandbyJobBrief } from "./voiceBrief";
import { inboxItemFromVoice } from "./voiceInbox";
import { DEFAULT_DUTY_STANDBY, type VoiceDeskSnapshot } from "./voiceDesk";
import type { CharterRequest, DutyStandbyState, DutyStandbyWindow, SceneryAirport } from "./types";
import { isCharterInboxItem } from "./voiceDesk";

function newWindowId(now: Date): string {
  return `stb-win-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e3).toString(36)}`;
}

export function emptyDutyStandby(): DutyStandbyState {
  return { ...DEFAULT_DUTY_STANDBY, windows: [], seenInboxIds: [] };
}

export function standbyWindowActive(windows: DutyStandbyWindow[], now: Date): boolean {
  if (!windows.length) return true;
  const t = now.getTime();
  return windows.some((w) => {
    const start = Date.parse(w.startUtc);
    const end = Date.parse(w.endUtc);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
    return t >= start && t <= end;
  });
}

export function shouldStandbyCall(input: {
  standby: DutyStandbyState;
  voiceEnabled: boolean;
  /** Prefer/force text still notifies when Voice Agent is off. */
  textChannelEnabled?: boolean;
  deskUnlocked: boolean;
  now?: Date;
}): boolean {
  if (!input.voiceEnabled && !input.textChannelEnabled) return false;
  if (!input.standby.armed) return false;
  if (!input.deskUnlocked) return false;
  return standbyWindowActive(input.standby.windows, input.now ?? appNow());
}

export function armDutyStandby(
  current: DutyStandbyState,
  seenInboxIds: string[],
  now = appNow(),
): DutyStandbyState {
  return {
    ...current,
    armed: true,
    armedAtUtc: now.toISOString(),
    seenInboxIds: [...new Set(seenInboxIds)],
  };
}

export function disarmDutyStandby(current: DutyStandbyState): DutyStandbyState {
  return {
    ...current,
    armed: false,
    armedAtUtc: undefined,
  };
}

export function upsertStandbyWindow(
  current: DutyStandbyState,
  window: Omit<DutyStandbyWindow, "id"> & { id?: string },
  now = appNow(),
): DutyStandbyState {
  const row: DutyStandbyWindow = {
    id: window.id || newWindowId(now),
    startUtc: window.startUtc,
    endUtc: window.endUtc,
    label: window.label ?? "",
  };
  const rest = current.windows.filter((w) => w.id !== row.id);
  return { ...current, windows: [...rest, row].sort((a, b) => a.startUtc.localeCompare(b.startUtc)) };
}

export function removeStandbyWindow(current: DutyStandbyState, id: string): DutyStandbyState {
  return { ...current, windows: current.windows.filter((w) => w.id !== id) };
}

export type StandbyJobHint = {
  originIcao: string;
  destIcao: string;
  client?: string;
  aircraftId?: string;
  registration?: string;
  sourceId: string;
  /** Inbox mint / Fetch / trickle vs an explicit generator / override accept. */
  cause?: "inbox" | "accepted";
};

export function newCharterJobs(inbox: CharterRequest[], seenIds: string[]): CharterRequest[] {
  const seen = new Set(seenIds);
  return inbox.filter((r) => isCharterInboxItem(r) && r.status === "new" && !seen.has(r.id));
}

export function evaluateStandbyJobs(input: {
  standby: DutyStandbyState;
  inbox: CharterRequest[];
  accepted?: StandbyJobHint | null;
}): StandbyJobHint[] {
  const jobs: StandbyJobHint[] = newCharterJobs(input.inbox, input.standby.seenInboxIds).map((r) => ({
    originIcao: r.originIcao,
    destIcao: r.destIcao,
    client: r.clientName,
    aircraftId: r.suggestedAircraftId,
    sourceId: r.id,
    cause: "inbox",
  }));
  if (input.accepted && !input.standby.seenInboxIds.includes(input.accepted.sourceId)) {
    jobs.push({ ...input.accepted, cause: input.accepted.cause ?? "accepted" });
  }
  return jobs;
}

export type StandbyCallResult = {
  standby: DutyStandbyState;
  item: CharterRequest;
  brief: string;
  job: StandbyJobHint;
};

/** Build a Standby Inbox item + brief. Caller persists inbox + standby. */
export function buildStandbyCall(input: {
  snapshot: VoiceDeskSnapshot;
  scenery: SceneryAirport[];
  standby: DutyStandbyState;
  job: StandbyJobHint;
  now?: Date;
}): StandbyCallResult {
  const now = input.now ?? appNow();
  const brief = composeStandbyJobBrief(input.snapshot, input.job);
  // Job card only — never paste the phone brief / CREW–DISPATCH turns into Inbox.
  const item = inboxItemFromVoice({
    snapshot: input.snapshot,
    scenery: input.scenery,
    phoneLeg: true,
    draft: {
      userText: `Duty Standby — new trip ${input.job.originIcao}–${input.job.destIcao}`,
      reply: brief,
      kind: "standby_call",
      originIcao: input.job.originIcao,
      destIcao: input.job.destIcao,
      clientName: input.job.client,
      aircraftId: input.job.aircraftId,
      now,
    },
  });
  return {
    brief,
    item,
    job: input.job,
    standby: {
      ...input.standby,
      seenInboxIds: [...new Set([...input.standby.seenInboxIds, input.job.sourceId, item.id])],
      lastCallInboxId: item.id,
    },
  };
}
